import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Alert
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import AddIcon from '@mui/icons-material/Add';
import { useQuery, useMutation, gql } from '@apollo/client';
import dayjs, { Dayjs } from 'dayjs';
import CreateTaskDialog from './CreateTaskDialog';
import { DeleteTaskDialog } from './DeleteTaskDialog';
import TaskList from './TaskList';
import { parseBackendDate } from './utils';

export const GET_TASKS = gql`
  query GetTasks($archived: Boolean) {
    getTasks(archived: $archived) {
      id
      title
      status
      endDate
      createdAt
      updatedAt
    }
  }
`;

export const CREATE_TASK = gql`
  mutation CreateTask($input: TaskInput!) {
    createTask(input: $input) {
      id
      title
      status
      endDate
    }
  }
`;

export const UPDATE_TASK = gql`
  mutation UpdateTask($id: ID!, $input: TaskInput!) {
    updateTask(id: $id, input: $input) {
      id
      status
      endDate
    }
  }
`;

export const DELETE_TASK = gql`
  mutation DeleteTask($id: ID!) {
    deleteTask(id: $id)
  }
`;

const refetchOptions = {
  refetchQueries: ['GetTasks', 'GetTasksHistory']
};

const TaskSection: React.FC = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [deleteTargetId, setDeleteTargetId] = useState<string | null>(null);
  const { data, loading, error } = useQuery(GET_TASKS, {
    variables: { archived: false },
    fetchPolicy: 'cache-and-network'
  });

  const [createTask] = useMutation(CREATE_TASK, refetchOptions);
  const [updateTask] = useMutation(UPDATE_TASK, refetchOptions);
  const [deleteTask] = useMutation(DELETE_TASK, refetchOptions);

  const handleCreateTask = (title: string, dueDate: Dayjs | null) => {
    const tempId = `temp-${Date.now()}`;
    createTask({
      variables: {
        input: {
          title,
          status: 'NOT_STARTED',
          archived: false,
          endDate: dueDate ? dueDate.toISOString() : null
        }
      },
      update(cache, { data: mutationData }) {
        const newTask = mutationData?.createTask || {
          __typename: 'Task',
          id: tempId,
          title,
          status: 'NOT_STARTED',
          archived: false,
          endDate: dueDate ? dueDate.toISOString() : null,
          createdAt: new Date().toISOString(),
          updatedAt: new Date().toISOString(),
        };

        const existing: any = cache.readQuery({ query: GET_TASKS, variables: { archived: false } });
        if (existing) {
          const filtered = existing.getTasks.filter((t: any) => t.id !== tempId);
          cache.writeQuery({
            query: GET_TASKS,
            variables: { archived: false },
            data: {
              getTasks: [...filtered, newTask]
            }
          });
        }
      }
    }).catch((err) => {
      console.error('Create task failed:', err);
    });
  };

  const nextStatus = (current: string) => {
    if (current === 'NOT_STARTED') return 'STARTED';
    if (current === 'STARTED') return 'FINISHED';
    return 'NOT_STARTED';
  };

  const handleToggleStatus = (task: any) => {
    const targetStatus = nextStatus(task.status);
    updateTask({
      variables: {
        id: task.id,
        input: { status: targetStatus }
      },
      optimisticResponse: {
        updateTask: {
          __typename: 'Task',
          id: task.id,
          status: targetStatus,
          endDate: task.endDate,
        }
      }
    }).catch((error) => {
      console.error('Update failed:', error);
    });
  };

  const handleConfirmDelete = () => {
    if (!deleteTargetId) return;
    deleteTask({
      variables: { id: deleteTargetId },
      update(cache) {
        const existing: any = cache.readQuery({ query: GET_TASKS, variables: { archived: false } });
        if (existing) {
          cache.writeQuery({
            query: GET_TASKS,
            variables: { archived: false },
            data: {
              getTasks: existing.getTasks.filter((t: any) => t.id !== deleteTargetId)
            }
          });
        }
      }
    }).catch((error) => {
      console.error('Delete failed:', error);
    });
    setDeleteTargetId(null);
  };

  if (loading) return <CircularProgress />;
  if (error) return <Alert severity="error">Failed to load tasks: {error.message}</Alert>;

  const tasks = data?.getTasks || [];
  if (tasks.length === 0) {
    return (
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Tasks</Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setIsDialogOpen(true)}
              size="small"
            >
              Add Task
            </Button>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 4 }}>
            No tasks found.
          </Typography>
          <CreateTaskDialog
            open={isDialogOpen}
            onClose={() => setIsDialogOpen(false)}
            onCreate={handleCreateTask}
          />
        </Paper>
      </LocalizationProvider>
    );
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper sx={{ p: 3, height: '100%', borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 'bold' }}>Tasks</Typography>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setIsDialogOpen(true)}
            size="small"
          >
            Add Task
          </Button>
        </Box>

        <TaskList
          tasks={tasks}
          onToggleStatus={handleToggleStatus}
          onDelete={setDeleteTargetId}
        />

        <CreateTaskDialog
          open={isDialogOpen}
          onClose={() => setIsDialogOpen(false)}
          onCreate={handleCreateTask}
        />

        <DeleteTaskDialog
          open={!!deleteTargetId}
          onConfirm={handleConfirmDelete}
          onCancel={() => setDeleteTargetId(null)}
        />
      </Paper>
    </LocalizationProvider>
  );
};

export default TaskSection;
